<template>
  <div>
    <div v-show="isLoading" class="text-center p-5">
      <dp-loading />
    </div>
    <ValidationObserver v-show="!isLoading" ref="form">
    <form @submit.prevent="onSubmit">
      <BaseRadioSwitch
        :key="keyItem('pet-species')"
        v-if="!petHasALicense"
        v-model="form.type"
        name="pet-species"
        :label="$t('dp.petPanel.whatKindOfPet')"
        :options="[
          { text: $t('dp.generic.dog'), value: 'D' },
          { text: $t('dp.generic.cat'), value: 'C' },
        ]"
        validation-rules="required"
        @change="onTypeChange"
      />
      <div v-else class="text-dark">
        Type:
        <span class="font-weight-bolder">{{ pet.type === 'DOG' ? $t('dp.generic.dog') : $t('dp.generic.cat') }}</span>
      </div>

      <span v-if="form.type === 'C'">
        <ValidationProvider v-slot="{ invalid: catTypeInvalid }" rules="required">
          <b-form-group id="pet-type-group" :label="$t('dp.petPanel.chooseOne')" label-for="pet-type-input">
            <b-form-radio-group
              :key="keyItem('pet-type-input')"
              id="pet-type-input"
              v-model="form.isIndoor"
              name="pet-type-radios"
              :options="petTypeOptions"
              aria-describedby="pet-type-feedback"
              stacked
            >
              <div id="pet-type-feedback" class="text-danger" role="alert">
                <small>
                  {{ $t(catTypeInvalid ? 'dp.petPanel.petCatOptionsRequired' : '', { catName: form.name || 'your cat' }) }}
                </small>
              </div>
            </b-form-radio-group>
          </b-form-group>
        </ValidationProvider>
      </span>

      <div class="mt-3">
        <DpBaseInput
          :key="keyItem('pet-name')"
          id="pet-name"
          v-model.trim="form.name"
          name="pet-name"
          type="text"
          :label="$t('dp.petPanel.petsName')"
          :placeholder="$t('dp.petPanel.namePlaceholder')"
          :validation-rules="{
            required: true,
            allowedSpecialChar: true,
            max: 50,
            petNameExists: { existingPetNames: existingPetsArr },
            inactivePetNames: { inactivePetNames: inactivePetNames, customerSupportPhone: customerSupportPhone },
            duplicatePet: { name: duplicatePetName, message: duplicatePetMessage },
          }"
          @focus="showPetNameEditWarning = true"
        />
        <b-alert v-if="mode === 'edit' && showPetNameWarning"
                 class="mt-3"
                 show
                 variant="info"
        >
          {{ $t('dp.petPanel.petNameEditWarning') }}
        </b-alert>
      </div>

      <div v-if="isBreedEditable && isEditingBreed" class="mt-3">
        <BaseRadioSwitch
          :key="keyItem('breed-known')"
          v-model="form.breedKnown"
          name="breed-known"
          :label="$t('dp.petPanel.knowBreed')"
          :options="[
            { text: $t('dp.generic.yes'), value: true },
            { text: $t('dp.generic.no'), value: false },
          ]"
          validation-rules="required"
          @change="clearBreed"
        />
        <div v-if="form.breedKnown" class="mt-3">
          <vue-simple-suggest
            :key="keyItem('breed-suggestions')"
            id="breed-suggestions"
            ref="breedSuggester"
            v-model="breedDisplayText"
            name="pet-breed-id"
            mode="input"
            type="text"
            :filter-by-query="true"
            :filter="breedSelectorFilter"
            :list="breeds"
            :styles="autoCompleteStyle"
            :destyled="true"
            :prevent-submit="true"
            display-attribute="name"
            value-attribute="name"
            :nullable-select="true"
            :min-length="1"
            :debounce="200"
            @select="selectedBreed"
          >
            <DpBaseInput
              :key="keyItem('pet-breed')"
              id="pet-breed"
              v-model="breedDisplayText"
              name="pet-breed"
              type="text"
              :placeholder="$t('dp.petPanel.breedPlaceholder')"
              :validation-rules="{ required: true, max: 255, breedAllowedChars: true }"
              autocomplete="off"
            >
              <template v-slot:label>
                <div class="d-flex align-items-center">
                  <label for="pet-breed" class="input-label">
                    {{$t('dp.petPanel.whatBreed')}}
                  </label>
                  <a id="popover-breed-info" class="icon-color"><fa-icon :icon="['fas', 'circle-info']"/></a>
                  <b-popover target="popover-breed-info" triggers="hover focus" placement="top">
                    <span v-html="$t('dp.petPanel.breedInfoPanel')"></span>
                  </b-popover>
                </div>
              </template>
            </DpBaseInput>
          </vue-simple-suggest>
        </div>
      </div>
      <div v-else class="text-dark mt-3">
        Breed:
        <span class="font-weight-bolder">{{ breedDisplayText }}</span>
        <b-link v-if="isBreedEditable && !isEditingBreed" @click="isEditingBreed = true" class="ml-2">{{$t('dp.petPanel.breedEditLink')}}</b-link>
      </div>

      <div v-if="!petHasALicense" class="mt-3">
        <BaseRadioSwitch
          :key="keyItem('pet-sex')"
          v-model="form.gender"
          name="pet-sex"
          :label="`${$t('dp.petPanel.whatGender')}`"
          :options="[
            { text: $t('dp.generic.male'), value: 'M' },
            { text: $t('dp.generic.female'), value: 'F' },
          ]"
          validation-rules="required"
        />
      </div>
      <div v-else class="text-dark mt-3">
        Sex:
        <span class="font-weight-bolder">{{ form.gender === 'M' ? $t('dp.generic.male') : $t('dp.generic.female') }}</span>
      </div>

      <!--   Spay/Neutered   -->
      <div v-if="mode === 'edit'" class="mt-3">
        <BaseRadioSwitch
          :key="keyItem('pet-neutered')"
          id="pet-neutered"
          v-if="!petHasALicense && !petHasNeuterProof"
          v-model="editForm.neutered"
          name="pet-neutered"
          :label="'Spay/Neutered'"
          :options="[
            { text: 'Yes', value: true },
            { text: 'No', value: false },
          ]"
        />
        <div v-else class="text-dark">
          Spay/Neutered:
          <span class="font-weight-bolder">{{ isNeutered }}</span>
        </div>
      </div>

      <!--   Microchip Number   -->
      <div v-if="mode === 'edit'" class="mt-3">
        <DpBaseInput
          :key="keyItem('pet-microchip')"
          id="pet-microchip"
          v-model="editForm.microchip"
          name="pet-microchip"
          type="text"
          :label="'Microchip Number'"
          :validation-rules="{ required: false, max: 255 }"
        />
      </div>

      <!--   Tattoo #   -->
      <div v-if="mode === 'edit'" class="mt-3">
        <DpBaseInput
          :key="keyItem('pet-tattoo')"
          id="pet-tattoo"
          v-model="editForm.tattoo"
          name="pet-tattoo"
          type="text"
          :label="'Tattoo #'"
          :validation-rules="{ required: false, max: 255 }"
        />
      </div>

      <div v-if="!petHasALicense" class="mt-3">
        <BaseRadioSwitch
          :key="keyItem('pet-age')"
          id="pet-age"
          v-model="form.dobIsApproximate"
          name="pet-age"
          :label="`${$t('dp.petPanel.knowBirthDate')}`"
          :options="[
            { value: false, text: $t('dp.generic.yes') },
            { value: true, text: $t('dp.generic.no') },
          ]"
          validation-rules="required"
        />
      </div>

      <div v-if="form.dobIsApproximate === false">
        <div v-if="!petHasALicense" class="mt-3">
          <dp-date-input
            :key="keyItem('birthdate')"
            v-model="form.birthdate"
            :label="`${$t('dp.petPanel.birthdate')}`"
            :original-date="mode === 'edit' ? form.birthdate : null"
            :validate-on-load="mode === 'edit' ? true : null"
            :date-required="form.dobIsApproximate === false"
            date-type="birthday"
            @existingDateError="setExistingError"
          />
        </div>
        <div v-else-if="pet && pet.birthday" class="text-dark">
          Birthdate:
          <span class="font-weight-bolder">{{ `${$t(displayBirthday.month)} ${displayBirthday.day}, ${displayBirthday.year}` }}</span>
        </div>
      </div>
      <div v-if="form.dobIsApproximate">
        <div v-if="!petHasALicense" class="mt-3">
          <ValidationProvider
            v-slot="{ errors, valid, validated, dirty }"
            ref="validationProvider"
            name="birthdate-approximate"
            :rules="form.dobIsApproximate ? 'required' : null"
          >
            <b-form-group id="birthdate-approximate-group" :label="$t('dp.petPanel.approximateAge')" label-for="birthdate-approximate">
              <b-form-select
                :key="keyItem('birthdate-approximate')"
                id="birthdate-approximate"
                v-model="age"
                name="birthdate-approximate"
                :options="[
                  { value: null, text: '' },
                  { value: 3, text: $t('dp.ageOptions.underSixMonths') },
                  { value: 9, text: $t('dp.ageOptions.sixMonthsToOneYear') },
                  { value: 12, text: $t('dp.ageOptions.oneYear') },
                  ...Array.from({ length: 15 }, (_, i) => {
                    return { value: (i + 2) * 12, text: $t('dp.ageOptions.timesYears', { yearNumber: i + 2 }) };
                  }),
                  { value: 17 * 12, text: $t('dp.ageOptions.seventeenPlusYears') },
                ]"
                v-bind="$attrs"
                :class="errors[0] ? 'is-invalid' : ''"
                :state="dirty || validated ? valid : null"
                aria-describedby="birthdate-invalid-feedback"
                autocomplete="off"
                v-on="$listeners"
              />
              <b-form-invalid-feedback id="birthdate-invalid-feedback" role="alert" v-html="errors[0]" />
            </b-form-group>
          </ValidationProvider>
        </div>
        <div v-else class="text-dark">
          Approximate Age:
          <span class="font-weight-bolder">
            <span v-if="age === 3">{{ $t('dp.ageOptions.underSixMonths') }}</span>
            <span v-else-if="age === 9">{{ $t('dp.ageOptions.sixMonthsToOneYear') }}</span>
            <span v-else-if="age === 12">{{ $t('dp.ageOptions.oneYear') }}</span>
            <span v-else-if="age === 17 * 12">{{ $t('dp.ageOptions.seventeenPlusYears') }}</span>
            <span v-else>{{ $t('dp.ageOptions.timesYears', { yearNumber: Math.floor(age / 12) }) }}</span>
          </span>
        </div>
      </div>

      <!--   Identifying Markings #   -->
      <div v-if="mode === 'edit'" class="mt-3">
        <DpBaseInput
          :key="keyItem('pet-markings')"
          id="pet-markings"
          v-model="editForm.markings"
          name="pet-markings"
          type="text"
          :label="'Identifying Markings'"
          :validation-rules="{ required: false, max: 255 }"
        />
      </div>

      <!--   Coat   -->
      <div v-if="mode === 'edit'" class="mt-3">
        <DpBaseInput
          :key="keyItem('pet-coat')"
          id="pet-coat"
          v-model="editForm.coat"
          name="pet-coat"
          type="text"
          :label="'Coat'"
          :validation-rules="{ required: false, max: 255 }"
        />
      </div>

      <div class="mt-3">
        <ValidationProvider v-slot="{ errors, valid, validated, dirty }" ref="validationProvider" name="color-picker" rules="required">
          <color-picker
            :key="keyItem('color-picker')"
            ref="colorPicker"
            v-model="form.colors"
            aria-describedby="color-invalid-feedback"
            :label="`${$t('dp.petPanel.whatColor')}`"
            :class="errors[0] ? 'is-invalid' : ''"
            :state="dirty || validated ? valid : null"
            :colors="[
              { id: 1, name: $t('dp.colors.white'), hex: '#FFFFFF' },
              { id: 2, name: $t('dp.colors.lightGrey'), hex: '#C5C5C5' },
              { id: 3, name: $t('dp.colors.grey'), hex: '#929088' },
              { id: 4, name: $t('dp.colors.darkGrey'), hex: '#7D7D7D' },
              { id: 5, name: $t('dp.colors.black'), hex: '#000000' },
              { id: 6, name: $t('dp.colors.cream'), hex: '#F6EBD7' },
              { id: 7, name: $t('dp.colors.yellow'), hex: '#E7DDBC' },
              { id: 8, name: $t('dp.colors.apricot'), hex: '#EDD098' },
              { id: 9, name: $t('dp.colors.golden'), hex: '#E6B560' },
              { id: 10, name: $t('dp.colors.tan'), hex: '#BB8E4F' },
              { id: 11, name: $t('dp.colors.orange'), hex: '#D88440' },
              { id: 12, name: $t('dp.colors.red'), hex: '#AB5D35' },
              { id: 13, name: $t('dp.colors.rust'), hex: '#834620' },
              { id: 14, name: $t('dp.colors.lightBrown'), hex: '#7E5C41' },
              { id: 15, name: $t('dp.colors.brown'), hex: '#745025' },
              { id: 16, name: $t('dp.colors.darkBrown'), hex: '#51391D' },
              { id: 17, name: $t('dp.colors.lightSilver'), hex: '#D4D4DB' },
              { id: 18, name: $t('dp.colors.silver'), hex: '#A6AAAE' },
              { id: 19, name: $t('dp.colors.lilac'), hex: '#89807D' },
              { id: 20, name: $t('dp.colors.blue'), hex: '#596674' },
              { id: 21, name: $t('dp.colors.darkBlue'), hex: '#373833' },
            ]"
          />
          <b-form-invalid-feedback id="color-invalid-feedback" role="alert" v-html="errors[0]" />
        </ValidationProvider>
      </div>

      <!--   Biography   -->
      <div v-if="mode === 'edit'" class="mt-3">
        <DpBaseTextArea
          :key="keyItem('pet-biography')"
          v-model="editForm.biography"
          name="pet-biography"
          :rows="5"
          :max-rows="10"
          :label="'Pet Biography'"
          :validation-rules="{ required: false, max: 65535 }"
        />
      </div>

      <!--   Additional Notes   -->
      <div v-if="mode === 'edit'" class="mt-3">
        <DpBaseTextArea
          :key="keyItem('pet-notes')"
          v-model="editForm.notes"
          name="pet-notes"
          :rows="5"
          :max-rows="10"
          :label="'Additional Notes'"
          :validation-rules="{ required: false, max: 65535 }"
        />
      </div>

      <br />
      <slot></slot>
      <b-button block type="submit" :disabled="isDisabled" variant="primary">
        {{ continueButtonLabel ? continueButtonLabel : $t('dp.generic.continue') }}
      </b-button>
    </form>
  </ValidationObserver>
  </div>
</template>

<script>
import ColorPicker from './SubComponents/ColorPicker';
import VueSimpleSuggest from 'vue-simple-suggest';
import DpBaseInput from '../BaseComponents/BaseInput/BaseInput';
import DpBaseTextArea from '../BaseComponents/BaseTextArea/BaseTextArea';
import BaseRadioSwitch from './SubComponents/BaseRadioSwitch';
import DpDateInput from '../DateInput/DateInput';
import DpLoading from '../Loading/Loading';
import { FocusableElementMixin } from '../../mixins/FocusableElementMixin';
import BreedApiService from '../../api/services/BreedApiService';
import { library } from '@fortawesome/fontawesome-svg-core';
import { icons } from './assets/fontawesome';
library.add(icons);
export default {
  name: 'AddAPet',
  components: {
    ColorPicker,
    VueSimpleSuggest,
    DpBaseInput,
    BaseRadioSwitch,
    DpDateInput,
    DpBaseTextArea,
    DpLoading,
  },
  mixins: [FocusableElementMixin],
  props: {
    pet: {
      type: Object,
      required: false,
      default: null,
    },
    duplicatePetName: {
      type: String,
      required: false,
      default: '',
    },
    duplicatePetMessage: {
      type: String,
      required: false,
      default: '',
    },
    existingName: {
      type: String,
      required: false,
      default: '',
    },
    existingPetNames: {
      type: Array,
      required: false,
      default: () => [],
    },
    inactivePetNames: {
      type: Array,
      required: false,
      default: () => [],
    },
    continueButtonLabel: {
      type: String,
      required: false,
      default: '',
    },
    customerSupportPhone: {
      type: String,
      required: false,
      default: '',
    },
    apiUrl: {
      type: String,
      required: false,
    }
  },
  data() {
    return {
      mode: 'add',
      form: {
        type: '',
        isIndoor: null,
        name: '',
        breedKnown: null,
        breed: '',
        gender: '',
        birthdate: {
          day: null,
          month: null,
          year: null,
        },
        dobIsApproximate: null,
        colors: [],
      },
      editForm: {
        neutered: null,
        microchip: '',
        tattoo: '',
        markings: '',
        coat: '',
        biography: '',
        notes: '',
      },
      allBreeds: [],
      breedPickedFromDropdown: null,
      age: '',
      autoCompleteStyle: {
        suggestions: 'custom-suggestions',
        suggestItem: 'custom-suggestion-item',
      },
      showPetNameWarning: false,
      genericErrorExists: false,
      petTypeOptions: [
        { text: this.$i18n.t('dp.petPanel.indoorCat'), value: true },
        { text: this.$i18n.t('dp.petPanel.outdoorCat'), value: false },
      ],
      isEditingBreed: true,
      isLoading: false,
    };
  },
  computed: {
    isBreedEditable() {
      return !this.petTypeIsRestrictedBreed && !this.petHasALicense;
    },
    breedDisplayText: {
      get() {
        if (typeof this.form.breed === 'object' && this.form.breed !== null) {
          return this.form.breed.name;
        }
        return this.form.breed;
      },
      set(newValue) {
        if (this.breedPickedFromDropdown && this.breedPickedFromDropdown.name !== newValue) {
          this.breedPickedFromDropdown = null;
        }
        this.form.breed = newValue;
      }
    },
    showPetNameEditWarning: {
      get: function () {
        return this.showPetNameWarning
      },
      set: function (newValue) {
        if (this.mode === 'edit') {
          this.showPetNameWarning = newValue
        }
      }
    },
    displayBirthday() {
      if (this.pet && this.pet.birthday) {
        let day = this.pet.birthday.day;
        let year = this.pet.birthday.year;
        let monthOptions = [
          'dp.input.dates.monthOptions.jan',
          'dp.input.dates.monthOptions.feb',
          'dp.input.dates.monthOptions.mar',
          'dp.input.dates.monthOptions.apr',
          'dp.input.dates.monthOptions.may',
          'dp.input.dates.monthOptions.jun',
          'dp.input.dates.monthOptions.jul',
          'dp.input.dates.monthOptions.aug',
          'dp.input.dates.monthOptions.sept',
          'dp.input.dates.monthOptions.oct',
          'dp.input.dates.monthOptions.nov',
          'dp.input.dates.monthOptions.dec',
        ];
        return { month: monthOptions[this.pet.birthday.month - 1], day: day, year: year };
      }
      return null;
    },
    existingPetsArr() {
      return this.existingPetNames ? [this.existingName, ...this.existingPetNames] : [this.existingName];
    },
    breeds() {
      const species = this.form.type === 'D' ? 'DOG' : 'CAT';
      let filteredBreeds = [];
      if (this.form.type && this.allBreeds.length) {
        filteredBreeds = this.allBreeds
          .filter(breed => breed.species === species)
          .map(breed => ({ name: breed.name, id: breed.id }));
      }
      return filteredBreeds
    },
    petHasALicense() {
      return this.pet && this.pet.licenses && this.pet.licenses.length;
    },
    petTypeIsRestrictedBreed() {
      return this.pet && this.pet.restrictedBreed;
    },
    petHasNeuterProof() {
      return this.pet && this.pet.hasValidNeuterProof;
    },
    isNeutered() {
      return this.pet?.details?.neutered ? this.$t('dp.generic.yes') : this.$t('dp.generic.no');
    },
    isDisabled() {
      return this.form.name
        ? [this.existingName, ...this.existingPetNames, ...this.inactivePetNames]
        .map(name => name.toLowerCase())
        .includes(this.form.name.toLowerCase()) || this.isDuplicatePet
        : false;
    },
    isDuplicatePet() {
      return this.duplicatePetName.toLowerCase() === this.form.name.toLowerCase();
    },
  },
  watch: {
    'form.type': function(val, old) {
      if (val === 'C' && old === 'D') {
        this.form.isIndoor = null;
      } else {
        this.form.isIndoor = this.pet?.indoorAnimal ?? null;
      }
    },
  },
  async mounted() {
    this.isLoading = true;
    try {
      await this.fetchBreeds();
      const breedSuggesterInput = this.$refs?.breedSuggester?.$refs?.inputSlot;
      breedSuggesterInput?.setAttribute('aria-label', `${this.$t('dp.petPanel.whatBreed').toString()} Suggester`);
      breedSuggesterInput?.setAttribute('id', 'suggested-breed-combobox');
      this.hydratePetForm();
    } finally {
      this.isLoading = false;
    }
  },
  methods: {
    keyItem(name) {
      return `${name}-${this.pet? this.pet.id: 'add'}`;
    },
    async fetchBreeds() {
      const breedApiService = new BreedApiService(this.apiUrl);
      this.allBreeds = await breedApiService.getPetBreeds();
    },
    setExistingError(val) {
      this.genericErrorExists = val;
    },
    clearBreed() {
      this.$nextTick(() => {
        this.form.breed = '';
      });
    },
    onSubmit() {
      //If a generic error exists don't submit the form.
      if (this.genericErrorExists) {
        return;
      }

      this.$refs.form.validate().then(async success => {
        if (this.form.dobIsApproximate) {
          const subtractAgeFromNow = new Date();
          subtractAgeFromNow.setMonth(subtractAgeFromNow.getMonth() - this.age);

          this.form.birthdate = {
            day: subtractAgeFromNow.getUTCDate(),
            month: subtractAgeFromNow.getUTCMonth() + 1,
            year: subtractAgeFromNow.getUTCFullYear(),
          };
        }

        if (success) {
          if (this.mode === 'add') {
            let formattedForm = JSON.parse(JSON.stringify(this.form));
            formattedForm.isIndoor = Boolean(formattedForm.isIndoor);
            formattedForm.birthday = formattedForm.birthdate;
            formattedForm.breed = this.getBreedValue();
            formattedForm.breedKnown = this.form.breedKnown;
            formattedForm.dobIsApproximate = Boolean(this.form.dobIsApproximate);
            delete formattedForm.birthdate;
            this.$emit('addPet', formattedForm);
          } else {
            let formToSubmit = { ...this.form, ...this.editForm };

            //EDIT PET API TAKES SLIGHTLY DIFFERENT ATTRIBUTE NAMES
            if (formToSubmit.type === 'D') {
              formToSubmit.type = 'DOG';
            } else if (formToSubmit.type === 'C') {
              formToSubmit.type = 'CAT';
            } else {
              formToSubmit.type = 'UNKNOWN';
            }
            if (formToSubmit.gender === 'M') {
              formToSubmit.gender = 'MALE';
            } else {
              formToSubmit.gender = 'FEMALE';
            }
            formToSubmit.color = formToSubmit.colors;
            delete formToSubmit.colors;
            formToSubmit.indoorAnimal = Boolean(formToSubmit.isIndoor);
            delete formToSubmit.isIndoor;
            formToSubmit.birthday = formToSubmit.birthdate;
            delete formToSubmit.birthdate;
            if (formToSubmit.neutered === null) {
              delete formToSubmit.neutered;
            }
            formToSubmit.breed = this.getBreedValue();
            formToSubmit.breedKnown = this.form.breedKnown;
            formToSubmit.dobIsApproximate = Boolean(this.form.dobIsApproximate);

            this.$emit('editPet', {
              petFormData: formToSubmit,
              petId: this.pet.id,
            });
          }
        } else {
          this.scrollToError(this.$refs.form);
        }
      })
      .catch(() => {
         this.scrollToError(this.$refs.form);
      });
    },
    onTypeChange() {
      this.form.breed = null;
      this.isEditingBreed = true;
      this.form.breedKnown = null;
    },
    selectedBreed(selected) {
      this.$nextTick(() => {
        this.breedPickedFromDropdown = selected;
        this.form.breed = selected;
      });
    },
    breedSelectorFilter(item, query) {
      if (item.id === null) {
        return true;
      }
      return item.name.toLowerCase().includes(query.toLowerCase());
    },

    getBreedValue() {
      return this.breedPickedFromDropdown ? this.breedPickedFromDropdown.name : this.form.breed;
    },

    hydratePetForm() {
      if (!this.pet) {
        return;
      }

      const hasBreed = this.pet.details?.breed && this.pet.details.breed.trim() !== '';
      if (!hasBreed) {
        return;
      }

      this.mode = 'edit';
      this.isEditingBreed = false;
      this.form.name = this.pet.name ?? '';
      this.form.breedKnown = this.pet.details.breedKnown;
      this.form.isIndoor = this.pet.indoorAnimal ?? null;
      this.form.dobIsApproximate = this.pet.dobIsApproximate ?? null;

      if (!this.pet.details) {
        return;
      }

      const breedName = this.pet.details.breed;
      const breed = this.allBreeds.find((b) => b.name === breedName);

      if (breed) {
        this.breedPickedFromDropdown = { id: breed.id, name: breed.name };
        this.form.breed = breed;
      } else if (this.pet.breed) {
        this.form.breed = this.pet.breed;
      }

      this.form.type = this.pet.details.type?.[0]?.toUpperCase() ?? '';
      this.form.gender = this.pet.details.gender?.[0]?.toUpperCase() ?? '';
      this.form.birthdate = this.pet.details.birthday ?? {};
      if (this.pet.dobIsApproximate) {
        this.age = this.pet.details.age?.months ?? null;
      }
      this.form.colors = this.pet.details.color;
      this.pet.details.color?.forEach(color => {
        this.$nextTick(() => {
          this.$refs.colorPicker.$refs?.[color]?.[0]?.$el?.click();
        });
      });
      this.editForm.neutered = this.pet.details.neutered ?? null;
      this.editForm.microchip = this.pet.details.microchip ? this.pet.details.microchip : '';
      this.editForm.tattoo = this.pet.details.tattoo ? this.pet.details.tattoo : '';
      this.editForm.markings = this.pet.details.markings ? this.pet.details.markings : '';
      this.editForm.coat = this.pet.details.coat ? this.pet.details.coat : '';
      this.editForm.biography = this.pet.details.biography ? this.pet.details.biography : '';
      this.editForm.notes = this.pet.details.notes ? this.pet.details.notes : '';
    },
  },
};
</script>

<style lang="scss">
@import '../../assets/styles/_variables';
//SimpleSuggest styles override Bootstrap validation styles. In order to get the two to work together we need to block existing and add new simple suggest styles...
.custom-suggestions {
  border-radius: 5px;
  border: 1px solid #aaa;
  box-shadow: 8px 8px 8px grey;
  z-index: 1000;
}
.custom-suggestion-item {
  border-bottom: 1px solid #aaa;
  padding: 0.5em;
  cursor: pointer;
}
.custom-suggestion-item.selected {
  background-color: $dp-navy;
  color: $dp-white;
}
.custom-suggestion-item.hover {
  background-color: $dp-blue;
  color: $dp-white;
}
.icon-color {
  color: $dp-navy;
}
//End simple suggest custom styles.

.age-select {
  width: 70px;
}
.custom-radio {
  margin-left: 20px;
}
</style>
